import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

// Format ROI as multiplier
const formatROI = (value) => `${(value / 100).toFixed(1)}x`;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-medium text-slate-900">{label}</p>
        <div className="mt-2 space-y-1 text-sm">
          <p className="text-rose-600">
            Low (-20%): <span className="font-mono">{formatROI(data.lowROI)}</span>
          </p>
          <p className="text-slate-600">
            Base: <span className="font-mono">{formatROI(data.baseROI)}</span>
          </p>
          <p className="text-emerald-600">
            High (+20%): <span className="font-mono">{formatROI(data.highROI)}</span>
          </p>
          <p className="text-navy-700 font-medium mt-1">
            Impact Range: <span className="font-mono">{formatROI(data.impact)}</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export const SensitivityTornado = ({ sensitivity, baseROI, title = "Sensitivity Analysis" }) => {
  if (!sensitivity || Object.keys(sensitivity).length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-slate-500">
            Run a calculation to see sensitivity analysis
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform sensitivity data for tornado chart
  const chartData = Object.entries(sensitivity)
    .map(([key, values]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      lowROI: values.low,
      highROI: values.high,
      baseROI: values.base,
      impact: values.impact,
      lowDelta: values.low - (baseROI || values.base),
      highDelta: values.high - (baseROI || values.base),
    }))
    .sort((a, b) => b.impact - a.impact); // Sort by impact (largest first)

  return (
    <Card data-testid="sensitivity-tornado-chart">
      <CardHeader>
        <CardTitle className="text-lg font-heading">{title}</CardTitle>
        <p className="text-sm text-slate-500">
          Impact of ±20% parameter variation on ROI
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => `${value > 0 ? '+' : ''}${(value / 100).toFixed(1)}x`}
                domain={['dataMin - 10', 'dataMax + 10']}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12, fill: '#64748b' }}
                width={90}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine x={0} stroke="#1e3a5f" strokeWidth={2} />
              
              {/* Low delta bars (negative direction) */}
              <Bar dataKey="lowDelta" stackId="a" radius={[4, 0, 0, 4]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`low-${entry.name || index}`}
                    fill={entry.lowDelta < 0 ? '#ef4444' : '#10b981'}
                  />
                ))}
              </Bar>
              
              {/* High delta bars (positive direction) */}
              <Bar dataKey="highDelta" stackId="b" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`high-${entry.name || index}`}
                    fill={entry.highDelta > 0 ? '#10b981' : '#ef4444'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="mt-4 flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-rose-500" />
            <span className="text-slate-600">Downside Risk (-20%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-500" />
            <span className="text-slate-600">Upside Potential (+20%)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SensitivityTornado;
